import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Link2, Loader2, RefreshCw, Unlink } from "lucide-react";
import { timeAgo } from "./ReplicaBanner";

const INPUT = "w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-white/30 disabled:opacity-50";
const CHANNELS = [
    { value: "dev", label: "dev" },
    { value: "stage", label: "stage" },
    { value: "prod", label: "prod" },
];

/** Spoke (replica / standalone): link this environment to the hub — request, wait for approval, claim the key. */
export function LinkToHubCard({ link, canWrite, onChanged }) {
    const [busy, setBusy] = useState(false);
    if (!link || link.state === "not_available") return null;

    const run = async (label, fn) => {
        setBusy(true);
        try { await fn(); if (label) toast.success(label); onChanged?.(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
    };
    const poll = () => run(null, async () => {
        const r = await api.post("/admin/sync/link/poll");
        toast[r.data.state === "linked" ? "success" : "message"](r.data.state === "linked" ? "Linked — sync key received" : `Still ${r.data.state}`);
    });
    const cancel = () => window.confirm("Withdraw the pending link request?") && run("Link request cancelled", () => api.post("/admin/sync/link/cancel"));
    const unlink = () => window.confirm("Forget the sync key obtained through the link request? Sync stops until you link again (revoke the environment on the hub too if the key may have leaked).") &&
        run("Unlinked", () => api.post("/admin/sync/link/unlink"));

    if (link.key_from_env) {
        return (
            <Shell title="Linked by deployment configuration" tone="emerald" testId="link-card">
                <p>This environment authenticates to <span className="font-mono text-white/80">{link.hub_url || "its hub"}</span> with <code>RADAR_SYNC_KEY</code> from its deployment configuration — no link request needed. To use a link request instead, remove the variable and redeploy.</p>
            </Shell>
        );
    }
    if (link.state === "linked") {
        return (
            <Shell title="Linked to the hub" tone="emerald" testId="link-card">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 text-emerald-200"><CheckCircle2 className="w-3.5 h-3.5" /> <span data-testid="link-state">linked</span></span>
                    <span>hub <span className="font-mono text-white/80">{link.hub_url}</span></span>
                    <span>as <span className="text-white/80" data-testid="link-name">{link.name}</span> · channel <span className="text-white/80" data-testid="link-channel">{link.channel}</span></span>
                    <span>key <span className="font-mono text-white/60">{link.key_prefix}…</span> · since {timeAgo(link.linked_at)}</span>
                    {link.env_id && <span className="font-mono text-white/40">{link.env_id}</span>}
                </div>
                {canWrite && (
                    <div className="mt-3">
                        <button onClick={unlink} disabled={busy} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-1.5 text-white/60 hover:bg-white/5 disabled:opacity-40" data-testid="link-unlink-btn">
                            <Unlink className="w-3.5 h-3.5" /> Unlink
                        </button>
                    </div>
                )}
            </Shell>
        );
    }
    if (link.state === "pending") {
        return (
            <Shell title="Link request pending" tone="amber" testId="link-card">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 text-amber-200"><Loader2 className="w-3.5 h-3.5 animate-spin" /> <span data-testid="link-state">waiting for hub approval</span></span>
                    <span>hub <span className="font-mono text-white/80">{link.hub_url}</span></span>
                    <span>as <span className="text-white/80">{link.name}</span> · channel <span className="text-white/80" data-testid="link-channel">{link.channel}</span></span>
                    <span>requested {timeAgo(link.requested_at)}{link.requested_by ? ` by ${link.requested_by}` : ""}</span>
                    <span className="font-mono text-white/40" data-testid="link-id">{link.link_id}</span>
                </div>
                <p className="mt-2 text-white/50">A hub super admin approves it under Settings → Radar Sync → Link requests. This page checks every 30 s and picks the key up automatically, then downloads the hub's dataset and keeps it in sync two-way.</p>
                {link.error && <div className="mt-2 text-amber-200" data-testid="link-error">{link.error}</div>}
                <div className="mt-3 flex items-center gap-2">
                    <button onClick={poll} disabled={busy} className="inline-flex items-center gap-1.5 rounded bg-white text-black px-3 py-1.5 font-medium hover:bg-white/90 disabled:opacity-40" data-testid="link-check-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Check now
                    </button>
                    {canWrite && <button onClick={cancel} disabled={busy} className="rounded border border-white/10 px-3 py-1.5 text-white/70 hover:bg-white/5 disabled:opacity-40" data-testid="link-cancel-btn">Cancel request</button>}
                </div>
            </Shell>
        );
    }
    return <LinkForm link={link} canWrite={canWrite} busy={busy} run={run} />;
}

function LinkForm({ link, canWrite, busy, run }) {
    const [hubUrl, setHubUrl] = useState(link.hub_url || "");
    const [name, setName] = useState(link.name && link.name !== "this-environment" ? link.name : (link.env_name !== "this-environment" ? link.env_name : window.location.host));
    const [url, setUrl] = useState(link.url || window.location.origin);
    const [channel, setChannel] = useState(link.channel || "dev");
    const submit = (e) => {
        e.preventDefault();
        run("Link request sent — waiting for the hub", () => api.post("/admin/sync/link", { hub_url: hubUrl || null, name, url, channel }));
    };
    const last = link.state !== "none" ? link : null;
    return (
        <Shell title="Link to hub" tone="sky" testId="link-card">
            <p className="mb-3">Register this environment on the hub without handling keys: choose its channel and send a link request. A hub super admin approves it; the sync key is then handed over automatically and kept here (encrypted) — also through a reset-to-blank.</p>
            {last && (
                <div className="mb-3 rounded border border-white/10 bg-white/5 px-3 py-2" data-testid="link-last">
                    Last request <span className="font-mono text-white/80" data-testid="link-state">{last.state}</span>{last.decided_at ? ` · ${timeAgo(last.decided_at)}` : ""}{last.decision_note ? ` · "${last.decision_note}"` : ""}{last.error ? ` · ${last.error}` : ""}
                </div>
            )}
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-2" data-testid="link-form">
                <label className="block md:col-span-2">
                    <span className="label-xs">Hub URL</span>
                    <input className={INPUT} value={hubUrl} onChange={(e) => setHubUrl(e.target.value)} placeholder="https://hub.your-company.example" required disabled={link.hub_url_from_env || !canWrite} data-testid="link-hub-url-input" />
                    {link.hub_url_from_env && <span className="text-[11px] text-white/40">from deployment configuration (RADAR_HUB_URL)</span>}
                </label>
                <label className="block">
                    <span className="label-xs">Environment name</span>
                    <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="stage.ai.sitecentric.ca" required minLength={2} disabled={!canWrite} data-testid="link-name-input" />
                </label>
                <label className="block">
                    <span className="label-xs">Public URL of this environment</span>
                    <input className={INPUT} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://stage.ai.sitecentric.ca" required disabled={!canWrite} data-testid="link-url-input" />
                </label>
                <label className="block md:col-span-2">
                    <span className="label-xs">Channel</span>
                    <select className={INPUT} value={channel} onChange={(e) => setChannel(e.target.value)} disabled={!canWrite} data-testid="link-channel-select">
                        {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </label>
                <div className="md:col-span-2 flex items-center gap-3">
                    <button type="submit" disabled={busy || !canWrite} className="inline-flex items-center gap-1.5 rounded bg-white text-black px-3 py-1.5 font-medium hover:bg-white/90 disabled:opacity-40" data-testid="link-submit-btn">
                        <Link2 className="w-3.5 h-3.5" /> Send link request
                    </button>
                    {!canWrite && <span className="text-white/40">Requires a super admin.</span>}
                </div>
            </form>
        </Shell>
    );
}

function Shell({ title, tone, testId, children }) {
    const ring = { emerald: "border-emerald-400/30", amber: "border-amber-400/30", sky: "border-sky-400/30" }[tone] || "border-white/10";
    return (
        <div className={`bg-[#0a0a0a] border ${ring} rounded-md p-6 text-xs text-white/60`} data-testid={testId}>
            <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                <div className="font-display text-lg text-white tracking-tight">{title}</div>
            </div>
            {children}
        </div>
    );
}
